import { IonButton, IonButtons, IonHeader, IonPage, IonTitle, IonToolbar } from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import Form, { FormInstance } from 'rc-field-form';
import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { t } from 'ttag';

import BackButton from '@components/BackButton';
import useLoading from '@hooks/useLoading';
import useToast from '@hooks/useToast';
import { getCompanyInfo } from '@state/company';
import { getEventInfo } from '@state/event';
import { getPeopleId } from '@state/user';

import Details, { Mode } from './Details';
import { useLead, useGetCustomFieldsConfig, useUpdateLead } from './queries';
import { Lead, Payload } from './service';

const selector = createSelector(
  getEventInfo,
  getCompanyInfo,
  getPeopleId,
  ({ id: eventId }, { id: companyId }, peopleId) => ({
    eventId,
    companyId,
    peopleId,
  })
);

const Edit = () => {
  const { eventId, companyId, peopleId } = useSelector(selector);
  const formRef = useRef<FormInstance>();
  const showToast = useToast();
  const { leadId } = useParams<{ leadId: string }>();
  const updateFn = useUpdateLead(eventId, companyId);
  const [mode, setMode] = useState(Mode.View);

  const [updatedLead, setUpdatedLead] = useState<Lead>();

  const { data: lead = updatedLead, isLoading } = useLead(eventId, companyId, leadId);
  const { data: customFieldsConfig } = useGetCustomFieldsConfig(eventId, companyId);
  const { presentLoading, dismissLoading } = useLoading(isLoading);

  const handleUpdate = useCallback(
    async (payload: Payload) => {
      if (!lead) {
        throw new Error('Lead Details not found');
      }
      try {
        presentLoading();
        const updated = await updateFn.mutateAsync({ leadId: lead.id, payload });
        setUpdatedLead(updated);
        showToast(t`Success! Lead saved.`);
        setMode(Mode.View);
      } finally {
        dismissLoading();
      }
    },
    [dismissLoading, lead, presentLoading, showToast, updateFn]
  );

  const EditButtons = useCallback(() => {
    if (lead?.capturedByPerson?.id !== peopleId) {
      return null;
    }

    if (mode === Mode.View) {
      return (
        <IonButtons slot="end" role="button">
          <IonButton
            fill="clear"
            // skipcq: JS-0417
            onClick={() => setMode(Mode.Edit)}
          >
            {t`Edit`}
          </IonButton>
        </IonButtons>
      );
    }

    if (mode === Mode.Edit) {
      return (
        <IonButtons slot="end" role="button">
          <Form>
            <IonButton
              // skipcq: JS-0417
              onClick={() => formRef.current?.submit()}
              role="button"
              fill="clear"
            >{t`Done`}</IonButton>
          </Form>
        </IonButtons>
      );
    }

    return null;
  }, [lead?.capturedByPerson?.id, mode, peopleId]);

  const setFormObject = (formObject: FormInstance) => {
    formRef.current = formObject;
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <BackButton />
          </IonButtons>
          <IonTitle>{t`Lead Detail`}</IonTitle>
          <EditButtons />
        </IonToolbar>
      </IonHeader>
      {lead && (
        <Details
          // skipcq: JS-0417
          getFormObject={setFormObject}
          onSave={handleUpdate}
          leadDetails={lead}
          customFieldsConfig={customFieldsConfig}
          mode={mode}
        />
      )}
    </IonPage>
  );
};

export default Edit;
